/**
 * JoinVideoSessionButton — shared CTA for joining a Zoom video session.
 *
 * Renders a real `<a>` anchor pointing to the normalized HTTPS Zoom join URL.
 * On mobile, the OS's universal-link handling opens the Zoom app when installed
 * and falls back gracefully to the Zoom web join page when it is not.
 *
 * Design principles:
 *  - No auto-open on mount; only navigates on an explicit user tap/click.
 *  - Href is always the HTTPS Zoom join URL — never a custom URL scheme.
 *  - Safe: rejects non-Zoom, non-HTTPS, and javascript:/data:/file: URLs.
 *  - Accessible: descriptive aria-label, keyboard-navigable.
 *  - Matches existing Mantine button/link conventions in medplum-ui.
 */

import { Button, Group, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconVideo, IconCopy } from '@tabler/icons-react';
import type { ButtonProps } from '@mantine/core';
import { getZoomJoinHref, isLikelyMobileOrTouchDevice } from '@/lib/zoomLinks';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface JoinVideoSessionButtonProps {
  /**
   * The Zoom HTTPS join URL (zoom.us/j/...).
   * If absent or invalid, the button is shown in a disabled/unavailable state.
   */
  joinUrl?: string | undefined | null;
  /**
   * Optional context for analytics / future logging. Not passed to the URL or
   * logged with PHI. Used only as a stable identifier for the appointment.
   */
  appointmentId?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  /**
   * When true, the join action is blocked (e.g. outside the client join window)
   * even if a valid Zoom URL is present.
   */
  disabled?: boolean;
  /**
   * When set, overrides the disabled tooltip message.
   * Defaults to "Video link unavailable" when joinUrl is invalid/absent.
   */
  disabledReason?: string;
  /** When true, show the "Copy link" icon button alongside the join button. */
  showCopyLink?: boolean;
  /** When true, show the mobile fallback helper text below the button. */
  showMobileFallbackHint?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Primary CTA for joining a telehealth/Zoom video session.
 *
 * - Valid join URL → renders an `<a>` anchor with `target="_blank" rel="noopener noreferrer"`.
 * - Mobile device → label "Open in Zoom" (universal link opens native app).
 * - Desktop → label "Join video session".
 * - No valid URL → disabled button with tooltip.
 */
export function JoinVideoSessionButton({
  joinUrl,
  appointmentId: _appointmentId,
  variant,
  size = 'sm',
  disabled = false,
  disabledReason,
  showCopyLink = true,
  showMobileFallbackHint,
}: JoinVideoSessionButtonProps): JSX.Element | null {
  const href = getZoomJoinHref(joinUrl ?? undefined);
  const isMobile = isLikelyMobileOrTouchDevice();
  const label = isMobile ? 'Open in Zoom' : 'Join video session';

  if (!href || disabled) {
    const unavailableMessage =
      disabledReason ?? (!href ? 'Video link unavailable' : 'Join is not available yet');
    return (
      <Tooltip label={unavailableMessage} withArrow>
        {/* span wrapper required by Tooltip when child is disabled */}
        <span>
          <Button
            size={size}
            variant={variant ?? 'filled'}
            leftSection={<IconVideo size={14} />}
            disabled
            data-testid="join-video-session-btn"
            aria-label={`${label} — ${unavailableMessage}`}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  const copyLink = (): void => {
    if (!navigator.clipboard?.writeText) {
      notifications.show({
        title: 'Copy failed',
        message: 'Clipboard is not available in this context.',
        color: 'red',
      });
      return;
    }
    void navigator.clipboard.writeText(href).then(
      () => {
        notifications.show({
          title: 'Link copied',
          message: 'Meeting link copied to clipboard.',
          color: 'green',
        });
      },
      () => {
        notifications.show({
          title: 'Copy failed',
          message: 'Could not copy meeting link.',
          color: 'red',
        });
      },
    );
  };

  const showHint = showMobileFallbackHint ?? isMobile;

  return (
    <Group gap="xs" align="flex-start" wrap="nowrap">
      <div>
        <Button
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          size={size}
          variant={variant ?? 'filled'}
          leftSection={<IconVideo size={14} />}
          data-testid="join-video-session-btn"
          aria-label={label}
        >
          {label}
        </Button>
        {showHint && (
          <Text size="xs" c="dimmed" mt={4} style={{ maxWidth: 220 }}>
            If Zoom doesn&apos;t open automatically, the meeting will open in your browser.
          </Text>
        )}
      </div>
      {showCopyLink && (
        <Tooltip label="Copy meeting link">
          <Button
            size={size}
            variant="light"
            color="gray"
            onClick={copyLink}
            aria-label="Copy meeting link"
            data-testid="copy-meeting-link-btn"
            px="xs"
          >
            <IconCopy size={14} />
          </Button>
        </Tooltip>
      )}
    </Group>
  );
}
